import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import { randUuid } from 'utils/functions';
import Log from 'utils/log';

const initialState = {
    /**
     * The list of registered layers.
     * @type {[key: string]: WcfUiSlidingLayerOptions}
     */
    layers: {},
    /**
     * The list of layers which are displayed in the main area.
     * @type {[key: string]: WcfUiMainAreaLayerOptions}
     */
    mainAreaLayers: {},
};

class SlidingLayersSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!SlidingLayersSliceHandler.instance) {
            SlidingLayersSliceHandler.instance = new SlidingLayersSliceHandler();
        }

        return SlidingLayersSliceHandler.instance;
    }

    constructor() {
        super('slidingLayersSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.MAIN_MENU_ITEM_SELECTED, () => {
            this.onMainMenuItemSelected();
        });

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                /**
                 * Create a sliding layer
                 * @param {object} state
                 * @param {{payload: WcfUiSlidingLayerOptions}} action
                 */
                addSlidingLayer(state, action) {
                    const lastAddedSlideId = action.payload.id;

                    state.layers[lastAddedSlideId] = { ...action.payload };

                    Log.debug(
                        'SLIDING LAYERS STORE',
                        `Registering layer for component "${
                            action.payload.componentPath ?? 'unspecified'
                        }" with id "${lastAddedSlideId}"`,
                    );
                },
                /**
                 * Remove a registered sliding layer.
                 * @param {object} state
                 * @param {{payload: string}} action The id of the removed layer.
                 */
                removeSlidingLayer(state, action) {
                    const sliderId = action.payload;
                    const currentLayers = state.layers;

                    if (typeof currentLayers[sliderId] === 'undefined') {
                        Log.notice(
                            'SLIDING LAYERS STORE',
                            `The slider with id "${sliderId}" cannot be removed because was not found.`,
                        );

                        return;
                    }

                    delete currentLayers[action.payload];

                    Log.debug('SLIDING LAYERS STORE', `Remove sliding layer with id "${sliderId}".`);

                    state.layers = { ...currentLayers };
                },
                /**
                 * Create a sliding layer for the main area.
                 * @param {object} state
                 * @param {{payload: WcfUiMainAreaLayerOptions}} action
                 */
                addMainAreaLayer(state, action) {
                    const lastAddedLayerId = action.payload.id;

                    state.mainAreaLayers[lastAddedLayerId] = { ...action.payload };

                    Log.debug(
                        'SLIDING LAYERS STORE',
                        `Registering main area layer for component "${
                            action.payload.componentPath ?? 'unspecified'
                        }" with id "${lastAddedLayerId}"`,
                    );
                },
                /**
                 * Remove a registered main area layer.
                 * @param {object} state
                 * @param {{payload: string}} action The id of the removed layer.
                 */
                removeMainAreaLayer(state, action) {
                    const sliderId = action.payload;
                    const currentLayers = state.mainAreaLayers;

                    if (typeof currentLayers[sliderId] === 'undefined') {
                        Log.notice(
                            'SLIDING LAYERS STORE',
                            `The main area layer with id "${sliderId}" cannot be removed because was not found.`,
                        );

                        return;
                    }

                    delete currentLayers[action.payload];

                    Log.debug('SLIDING LAYERS STORE', `Remove main area layer with id "${sliderId}".`);

                    state.mainAreaLayers = { ...currentLayers };
                },
            },
        });
    }

    /**
     * Open a sliding layer.
     * @param {WcfUiSlidingLayerOptions} options
     * @return {string} The id of the registered layer.
     */
    openSlidingLayer = (options) => {
        options.id = `sliding-layer-${randUuid()}`;

        this.dispatch('addSlidingLayer', options);

        if (typeof options.onOpen === 'function') {
            options.onOpen(options.id);
        }

        return options.id;
    };

    /**
     * Close a sliding layer.
     * @param {string} slidingLayerId - the id of the sliding layer
     */
    closeSlidingLayer = (slidingLayerId) => {
        const state = this.getOwnState();

        if (typeof state.layers[slidingLayerId] === 'undefined') {
            return;
        }

        const registeredLayer = state.layers[slidingLayerId];

        this.dispatch('removeSlidingLayer', slidingLayerId);

        if (typeof registeredLayer.onClose === 'function') {
            registeredLayer.onClose(slidingLayerId);
        }
    };

    /**
     * Open a sliding layer for main area.
     * @param {WcfUiMainAreaLayerOptions} options
     * @return {string} The id of the registered layer.
     */
    openMainAreaLayer = (options) => {
        options.id = `main-area-layer-${randUuid()}`;

        this.dispatch('addMainAreaLayer', options);

        return options.id;
    };

    /**
     * Close a sliding layer rendered in the main area.
     * @param {string} mainLayerId - the id of the sliding layer
     */
    closeMainAreaLayer = (mainLayerId) => {
        this.dispatch('removeMainAreaLayer', mainLayerId);
    };

    onMainMenuItemSelected = () => {
        const state = this.getOwnState();
        Object.values(state.layers).forEach((layer) => {
            this.closeSlidingLayer(layer.id);
            layer.onClose();
        });
    };
}

// the handler
export const slidingLayersSliceHandler = SlidingLayersSliceHandler.getInstance();
export const selectSlidingLayers = (state) => state.slidingLayersSlice.layers;
export const selectMainAreaLayers = (state) => state.slidingLayersSlice.mainAreaLayers;
export const { openSlidingLayer, closeSlidingLayer, openMainAreaLayer, closeMainAreaLayer } = slidingLayersSliceHandler;
